/**
 * Iter 149 — Campaign Playbooks page
 *
 * Route: `/app/marketing/playbooks`
 *
 * Library of reusable multi-channel content packs. Each playbook holds a
 * sequence of items (posts, stories, reels, blogs, emails, tasks) each with a
 * `day_offset` relative to an anchor date. One-click "Import into brand X"
 * clones every item into the brand's content calendar with proper dates and
 * spawns tasks assigned to the brand's content owner.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Layers, Plus, Send, Trash2, Edit3, Play, X, Search, Zap } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const ITEM_KINDS = ["post", "story", "reel", "blog", "email", "task"];
const CHANNELS = ["instagram", "facebook", "linkedin", "twitter", "youtube", "email", "whatsapp", "other"];

export default function CampaignPlaybooks() {
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms(activeWorkspaceId);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // playbook obj or {} for new
  const [importFor, setImportFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const canManage = can("marketing.manage") || can("brand.manage");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/campaign-playbooks?${params}`);
      setRows(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load playbooks"));
    } finally { setBusy(false); }
  }, [activeWorkspaceId, q]);
  useEffect(() => { load(); }, [load]);

  const doDelete = useCallback(async () => {
    if (!deleteFor) return;
    try {
      await api.delete(`/campaign-playbooks/${deleteFor.id}`);
      setRows((prev) => prev.filter((r) => r.id !== deleteFor.id));
      toast.success("Playbook deleted");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setDeleteFor(null); }
  }, [deleteFor]);

  return (
    <>
      <PageHeader
        breadcrumb="Marketing"
        title="Campaign playbooks"
        description="Reusable multi-channel content packs tied to marketable days. Import a playbook into any brand's calendar with one click — all posts, tasks and dates get spawned automatically."
        testid="playbooks-header"
        actions={
          canManage && (
            <Button className="rounded-none" onClick={() => setEditing({})} data-testid="playbooks-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New playbook
            </Button>
          )
        }
      />

      <div className="px-4 sm:px-6 md:px-10 pt-4">
        <div className="inline-flex items-center gap-1 border border-border px-2 h-9 max-w-md mb-4">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search playbook name, description, tags…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
            data-testid="playbooks-search"
          />
        </div>

        {busy && rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No playbooks yet"
            description="Bundle a reusable multi-channel content pack once, then import it into every brand that fits."
            icon={Layers}
            testid="playbooks-empty"
            action={canManage && <Button className="rounded-none" onClick={() => setEditing({})}>Create first playbook</Button>}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="playbooks-grid">
            {rows.map((p) => (
              <PlaybookCard
                key={p.id} p={p}
                canManage={canManage}
                onEdit={() => setEditing(p)}
                onImport={() => setImportFor(p)}
                onDelete={() => setDeleteFor(p)}
              />
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <PlaybookEditor
          wsId={activeWorkspaceId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === saved.id);
              return idx >= 0 ? [...prev.slice(0, idx), saved, ...prev.slice(idx + 1)] : [saved, ...prev];
            });
            setEditing(null);
          }}
        />
      )}
      {importFor && (
        <ImportDialog
          wsId={activeWorkspaceId}
          playbook={importFor}
          onClose={() => setImportFor(null)}
        />
      )}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => { if (!o) setDeleteFor(null); }}>
        <AlertDialogContent data-testid="playbook-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete playbook?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteFor?.name}&rdquo; will be removed from your library. Content items already imported into brand calendars keep their content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─────────────── Card ───────────────

function PlaybookCard({ p, canManage, onEdit, onImport, onDelete }) {
  return (
    <div className="border border-border bg-background hover:border-primary/60 transition p-3 flex flex-col gap-2" data-testid={`playbook-card-${p.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-snug line-clamp-2" data-testid={`playbook-name-${p.id}`}>{p.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="chip text-[10px]">{p.category}</span>
            <span>{(p.items || []).length} items</span>
            {p.anchor?.day_name && <span className="chip text-[10px]">🎯 {p.anchor.day_name}</span>}
            <span>{fmtRelative(p.created_at)}</span>
          </div>
        </div>
      </div>
      {p.description && <p className="text-[12px] text-muted-foreground line-clamp-3">{p.description}</p>}
      {(p.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {p.tags.slice(0, 6).map((t) => <span key={t} className="chip text-[10px]">#{t}</span>)}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto border-l-2 border-primary/40 pl-2">
        {(p.items || []).slice(0, 5).map((it) => (
          <div key={it.id} className="flex items-center gap-1">
            <span className="chip text-[9px]">{it.kind}</span>
            <span className="chip text-[9px]">{it.channel}</span>
            <span className="truncate">{it.title}</span>
            <span className="ml-auto font-mono">{it.day_offset >= 0 ? "+" : ""}{it.day_offset}d</span>
          </div>
        ))}
        {(p.items || []).length > 5 && <div className="text-[9px] italic">+{p.items.length - 5} more</div>}
      </div>
      <div className="flex items-center gap-1 mt-1">
        {canManage && (
          <Button size="sm" className="rounded-none h-8 flex-1" onClick={onImport} data-testid={`playbook-import-${p.id}`}>
            <Send className="w-3 h-3 mr-1" /> Import to brand
          </Button>
        )}
        {canManage && (
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={onEdit} title="Edit" data-testid={`playbook-edit-${p.id}`}>
            <Edit3 className="w-3 h-3" />
          </Button>
        )}
        {canManage && (
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={onDelete} title="Delete" data-testid={`playbook-delete-${p.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ─────────────── Editor (create + edit) ───────────────

function PlaybookEditor({ wsId, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    description: initial?.description || "",
    category: initial?.category || "seasonal",
    tags: (initial?.tags || []).join(", "),
    anchor: initial?.anchor || { kind: "none" },
    items: initial?.items || [{ kind: "post", channel: "instagram", title: "", description: "", day_offset: 0, hashtags: [], caption: "" }],
  }));
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setItem = (i, k, v) => setForm((prev) => ({
    ...prev,
    items: prev.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it),
  }));
  const addItem = () => setForm((prev) => ({
    ...prev,
    items: [...prev.items, { kind: "post", channel: "instagram", title: "", description: "", day_offset: (prev.items.at(-1)?.day_offset || 0) + 1, hashtags: [], caption: "" }],
  }));
  const removeItem = (i) => setForm((prev) => ({
    ...prev,
    items: prev.items.filter((_, idx) => idx !== i),
  }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.items.length) return toast.error("Add at least one item");
    const payload = {
      name: form.name, description: form.description, category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      anchor: form.anchor,
      items: form.items.map((it) => ({
        ...it,
        hashtags: Array.isArray(it.hashtags) ? it.hashtags : String(it.hashtags || "").split(",").map((h) => h.trim()).filter(Boolean),
        day_offset: Number(it.day_offset) || 0,
      })),
    };
    setBusy(true);
    try {
      const { data } = isNew
        ? await api.post(`/workspaces/${wsId}/campaign-playbooks`, payload)
        : await api.patch(`/campaign-playbooks/${initial.id}`, payload);
      toast.success(isNew ? "Playbook created" : "Playbook updated");
      onSaved(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="playbook-editor">
        <DialogHeader><DialogTitle>{isNew ? "New playbook" : "Edit playbook"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Name</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="playbook-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Category</Label>
              <Input value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="seasonal, evergreen, launch…" data-testid="playbook-category-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="playbook-desc-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Anchor kind</Label>
              <Select value={form.anchor?.kind || "none"} onValueChange={(v) => update("anchor", { ...form.anchor, kind: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="special_day">Special day</SelectItem>
                  <SelectItem value="custom_day">Custom day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Anchor name</Label>
              <Input value={form.anchor?.day_name || ""} onChange={(e) => update("anchor", { ...form.anchor, day_name: e.target.value })} placeholder="Diwali" data-testid="playbook-anchor-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Tags (comma)</Label>
              <Input value={form.tags} onChange={(e) => update("tags", e.target.value)} placeholder="diwali, festival" data-testid="playbook-tags-input" />
            </div>
          </div>

          {/* Items */}
          <div className="border border-border">
            <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center text-xs">
              <span className="font-semibold uppercase tracking-widest">Items ({form.items.length})</span>
              <Button size="sm" variant="outline" className="rounded-none ml-auto h-7" onClick={addItem} data-testid="playbook-add-item">
                <Plus className="w-3 h-3 mr-1" /> Add item
              </Button>
            </div>
            <div className="p-2 space-y-2 max-h-[40vh] overflow-y-auto">
              {form.items.map((it, i) => (
                <div key={i} className="border border-border p-2 space-y-2" data-testid={`playbook-item-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2">
                      <Label className="text-[9px] uppercase tracking-widest">Kind</Label>
                      <Select value={it.kind} onValueChange={(v) => setItem(i, "kind", v)}>
                        <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{ITEM_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[9px] uppercase tracking-widest">Channel</Label>
                      <Select value={it.channel} onValueChange={(v) => setItem(i, "channel", v)}>
                        <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-6">
                      <Label className="text-[9px] uppercase tracking-widest">Title</Label>
                      <Input value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} className="h-8 rounded-none text-xs" data-testid={`playbook-item-title-${i}`} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[9px] uppercase tracking-widest">Day</Label>
                      <Input type="number" value={it.day_offset} onChange={(e) => setItem(i, "day_offset", e.target.value)} className="h-8 rounded-none text-xs" data-testid={`playbook-item-offset-${i}`} />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button size="sm" variant="ghost" className="rounded-none h-8 w-full text-destructive" onClick={() => removeItem(i)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[9px] uppercase tracking-widest">Description</Label>
                      <Input value={it.description || ""} onChange={(e) => setItem(i, "description", e.target.value)} className="h-8 rounded-none text-xs" />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-widest">Hashtags (comma)</Label>
                      <Input value={Array.isArray(it.hashtags) ? it.hashtags.join(", ") : (it.hashtags || "")} onChange={(e) => setItem(i, "hashtags", e.target.value.split(",").map((h) => h.trim()).filter(Boolean))} className="h-8 rounded-none text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="playbook-save-btn">
            {busy ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Import to brand ───────────────

function ImportDialog({ wsId, playbook, onClose }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [ctx, setCtx] = useState(null); // { packages, projects }
  const [packageId, setPackageId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [anchorDate, setAnchorDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/brands`).then(({ data }) => {
      setBrands(data || []);
      setBrandId(data?.[0]?.id || "");
    }).catch(() => {});
  }, [wsId]);

  useEffect(() => {
    if (!brandId) { setCtx(null); return; }
    setCtx(null); setPackageId(""); setProjectId("");
    api.get(`/workspaces/${wsId}/brands/${brandId}/import-context`)
      .then(({ data }) => {
        setCtx(data);
        setPackageId(data.packages?.[0]?.id || "");
        if (!data.packages?.length) setProjectId(data.projects?.[0]?.id || "");
      })
      .catch(() => setCtx({ packages: [], projects: [] }));
  }, [wsId, brandId]);

  const submit = async () => {
    if (!brandId) return toast.error("Select a brand");
    setBusy(true);
    try {
      const { data } = await api.post(`/campaign-playbooks/${playbook.id}/import-to-brand`, {
        brand_id: brandId, anchor_date: anchorDate,
        package_id: packageId || null,
        project_id: !packageId && projectId ? projectId : null,
      });
      toast.success(`Imported ${data.content_item_ids.length} items + ${data.task_ids.length} task${data.task_ids.length === 1 ? "" : "s"} to ${data.brand.name}`);
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Import failed"));
    } finally { setBusy(false); }
  };

  const anchorHint = playbook.anchor?.day_date
    ? `Playbook's default anchor: ${playbook.anchor.day_name || "day"} · ${playbook.anchor.day_date}`
    : null;
  const hasProject = !!(packageId && ctx?.packages.find((p) => p.id === packageId)?.project_id) || !!projectId;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-none" data-testid="playbook-import-dialog">
        <DialogHeader><DialogTitle>Import playbook to brand</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-primary/30 bg-primary/5 p-2 text-xs">
            <div className="font-semibold">{playbook.name}</div>
            <div className="text-muted-foreground">{(playbook.items || []).length} items will be cloned into the brand&rsquo;s calendar.</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Brand</Label>
            <Select value={brandId} onValueChange={setBrandId}>
              <SelectTrigger className="h-9 rounded-none" data-testid="playbook-import-brand"><SelectValue placeholder="Choose brand" /></SelectTrigger>
              <SelectContent>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {ctx && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Package (resolves project for tasks)</Label>
                <Select value={packageId || "_none"} onValueChange={(v) => setPackageId(v === "_none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="playbook-import-package"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No package</SelectItem>
                    {ctx.packages.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.project_id ? "· has project" : "· no project"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!packageId && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Or project directly</Label>
                  <Select value={projectId || "_none"} onValueChange={(v) => setProjectId(v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-9 rounded-none" data-testid="playbook-import-project"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No project</SelectItem>
                      {ctx.projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className={`text-[11px] p-2 border ${hasProject ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700" : "border-amber-500/40 bg-amber-500/5 text-amber-700"}`}>
                {hasProject
                  ? "✓ Tasks will spawn in the selected project."
                  : "⚠ Pick a package/project to get tasks — otherwise only content items are created."}
              </div>
            </>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Anchor date</Label>
            <Input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} className="h-9 rounded-none" data-testid="playbook-import-date" />
            {anchorHint && <div className="text-[10px] text-muted-foreground mt-1">{anchorHint} — <button type="button" className="underline" onClick={() => setAnchorDate(playbook.anchor.day_date)}>use default</button></div>}
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            Each item is dated as <b>anchor + day_offset</b>.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !brandId} data-testid="playbook-import-submit">
            <Play className="w-3 h-3 mr-1" /> {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
